/* ==========================================================================
 * CART CUSTOM ENGINE — Order Protection + Free Shipping Bar
 * ==========================================================================
 * Two features sharing one cart-fetch and one cart-change listener:
 *   1. Order Protection: adds/removes tier variant based on subtotal
 *   2. Free Shipping Bar: progress toward the free-shipping threshold
 *
 * Both measure the PRODUCT subtotal EXCLUDING the Order Protection line.
 *
 * RENDERING MODEL: after one of our cart writes, refreshCartUI re-renders the
 * drawer, the header bubble and (on /cart) the cart sections from the server,
 * the same way Dawn does after its own writes. The Order Protection line is
 * rendered as a hidden row by the Liquid (drawer and /cart), the empty state
 * and the bubble count are garments-only in Liquid, and Dawn rebuilds its
 * cart-items element on every swap — so nothing here patches totals, row
 * indices, empty states or Dawn's item-count cache any more.
 *
 * --------------------------------------------------------------------------
 * MAINTENANCE (2026-09-23): nothing about the protection product lives here.
 *   - TIERS, PRICES, VARIANT IDs: read from the protection product itself.
 *     snippets/package-assurance.liquid renders the product's variants into a
 *     <script type="application/json" data-pa-config> block; each variant's
 *     Tier option is "OP-<subtotal ceiling in dollars>" or "OP-MAX". Add,
 *     remove or reprice tiers in the Shopify product admin only.
 *   - PRODUCT HANDLE and FREE SHIPPING THRESHOLD: theme settings (the
 *     "Order Protection & Free Shipping" group in config/settings_schema.json),
 *     read by the Liquid and passed through the same config block.
 *   - No config block on the page (widget not rendered) = only the shipping
 *     bar runs.
 * --------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // ---- config (from the Liquid-rendered JSON block) ------------------------
  // { handle, threshold, tiers: [{ id, max, price }] } — price in CENTS,
  // max = Infinity for OP-MAX. The drawer re-renders repeat the same block, so
  // re-parse only when the text changes.
  var PA_CONFIG = null;
  var PA_CONFIG_RAW = null;

  function readConfig() {
    var el = document.querySelector('[data-pa-config]');
    if (!el) return PA_CONFIG;
    var raw = el.textContent;
    if (PA_CONFIG && raw === PA_CONFIG_RAW) return PA_CONFIG;
    try {
      var parsed = JSON.parse(raw);
      var tiers = (parsed.tiers || []).map(function (t) {
        return { id: Number(t.id), max: (t.max == null) ? Infinity : Number(t.max), price: Number(t.price) };
      }).filter(function (t) {
        return t.id > 0 && !isNaN(t.max) && !isNaN(t.price);
      }).sort(function (a, b) { return a.max - b.max; });
      if (!tiers.length) return PA_CONFIG;
      PA_CONFIG = {
        handle: parsed.handle || 'package-assurance',
        threshold: Number(parsed.threshold) || 0,
        tiers: tiers
      };
      PA_CONFIG_RAW = raw;
    } catch (e) {
      console.warn('[CartCustom] bad data-pa-config JSON:', e);
    }
    return PA_CONFIG;
  }

  // Display price (in DOLLARS) for a tier.
  function priceForTier(tier) {
    return tier.price / 100;
  }

  var isBusy = false;
  var pendingRecompute = false;

  // ---- shared helpers -----------------------------------------------------

  function formatMoney(dollars) {
    return '$' + dollars.toFixed(dollars % 1 === 0 ? 0 : 2);
  }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  // A cart line is the protection line when it belongs to the configured
  // product — any variant of it, so a tier the JS does not know about (added
  // in admin a minute ago) is still recognised.
  function isPALine(item) {
    var cfg = readConfig();
    return !!cfg && item.handle === cfg.handle;
  }

  // Product subtotal in cents EXCLUDING the Order Protection line.
  function subtotalExcludingPA(cart) {
    var cents = 0;
    cart.items.forEach(function (item) {
      if (!isPALine(item)) { cents += item.final_line_price; }
    });
    return cents;
  }

  function tierForSubtotal(cents) {
    var cfg = readConfig();
    if (!cfg) return null;
    var dollars = cents / 100;
    for (var i = 0; i < cfg.tiers.length; i++) {
      if (dollars <= cfg.tiers[i].max) return cfg.tiers[i];
    }
    return cfg.tiers[cfg.tiers.length - 1];
  }

  function findPALine(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (isPALine(cart.items[i])) {
        return { key: cart.items[i].key, id: cart.items[i].variant_id, quantity: cart.items[i].quantity };
      }
    }
    return null;
  }

  // ========================================================================
  // FEATURE 1: ORDER PROTECTION
  // ========================================================================

  function addTier(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });
  }

  function setLineQuantity(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    });
  }

  function removeLineByKey(key) {
    return setLineQuantity(key, 0);
  }

  // fetch() resolves on a 422 from add.js/change.js; treat that as a failure
  // so reconcile() can re-sync the widget to what is really in the cart.
  function expectOk(label) {
    return function (res) {
      if (!res.ok) throw new Error(label + ' refused: HTTP ' + res.status);
      return res;
    };
  }

  // Re-render the cart surfaces from the server after one of OUR cart writes
  // (Order Protection add / remove / tier swap), the same way Dawn does after
  // its own writes. Mirrors Dawn's own targets:
  //   - drawer: `cart-drawer` IS a Section Rendering API target
  //     (sections/cart-drawer.liquid renders the snippet); swap the
  //     `.drawer__inner` contents exactly like CartDrawerItems does.
  //   - header bubble: `cart-icon-bubble`.
  //   - /cart page: the two main sections must be requested by their
  //     TEMPLATE ids (the data-id on the wrappers, e.g. "cart-items" /
  //     "cart-footer") against the current path, as cart.js does. Asking for
  //     the section FILE names renders them standalone without their blocks,
  //     which is what used to wipe the /cart footer after an OP change.
  function syncEmptyClass(fresh, live) {
    if (fresh && live) live.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
  }

  function refreshCartUI() {
    var targets = [];
    var drawerInner = document.querySelector('#CartDrawer .drawer__inner');
    if (drawerInner) targets.push({ section: 'cart-drawer', el: drawerInner, selector: '.drawer__inner' });
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) targets.push({ section: 'cart-icon-bubble', el: bubble, selector: '.shopify-section' });
    ['main-cart-items', 'main-cart-footer'].forEach(function (id) {
      var wrapper = document.getElementById(id);
      var contents = wrapper && wrapper.querySelector('.js-contents');
      if (wrapper && wrapper.dataset.id && contents) {
        targets.push({ section: wrapper.dataset.id, el: contents, selector: '.js-contents' });
      }
    });
    if (!targets.length) return Promise.resolve();

    var ids = targets.map(function (t) { return t.section; });
    // Plain fetch on purpose: with `Accept: application/json` a product URL
    // answers with the product JSON and /cart with the cart JSON, ignoring
    // `?sections=`. The Section Rendering API returns JSON regardless.
    var url = window.location.pathname + '?sections=' + ids.join(',');
    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        targets.forEach(function (t) {
          if (!data[t.section]) return;
          var doc = new DOMParser().parseFromString(data[t.section], 'text/html');
          var fresh = doc.querySelector(t.selector);
          if (fresh) t.el.innerHTML = fresh.innerHTML;
          // The empty-state class sits on the OUTER <cart-drawer>, outside the
          // swapped inner. Dawn toggles it from cart.item_count (which counts
          // the OP line); the Liquid keys it on garments, so copy the server's
          // verdict across after our own writes.
          if (t.section === 'cart-drawer') {
            syncEmptyClass(doc.querySelector('cart-drawer'), document.querySelector('cart-drawer'));
          } else if (t.selector === '.js-contents') {
            // /cart: <cart-items> and #main-cart-footer carry is-empty too.
            syncEmptyClass(doc.querySelector('cart-items'), document.querySelector('cart-items'));
            syncEmptyClass(doc.getElementById('main-cart-footer'), document.getElementById('main-cart-footer'));
          }
        });
      })
      .catch(function () {});
  }

  function reconcile(intentChecked) {
    if (isBusy) { pendingRecompute = true; return Promise.resolve(); }
    if (!readConfig()) return Promise.resolve();
    isBusy = true;
    setLoading(true);

    return getCart().then(function (cart) {
      var paLine = findPALine(cart);
      var subCents = subtotalExcludingPA(cart);
      var desiredTier = tierForSubtotal(subCents);

      if (!intentChecked || subCents <= 0 || !desiredTier) {
        if (paLine) return removeLineByKey(paLine.key).then(expectOk('remove')).then(refreshCartUI);
        return Promise.resolve();
      }
      if (paLine && paLine.id === desiredTier.id) {
        if (paLine.quantity === 1) return Promise.resolve();
        // Right tier, wrong quantity: repair in place, never add a second line.
        return setLineQuantity(paLine.key, 1).then(expectOk('repair')).then(refreshCartUI);
      }
      if (paLine) {
        // Tier swap: ADD the new tier first, then remove the old one, so the
        // bag never has zero protection lines mid-swap. If the add is refused
        // the old line stays; if the remove is refused the refresh shows the
        // real cart and the next sync repairs it.
        return addTier(desiredTier.id).then(expectOk('add'))
          .then(function () { return removeLineByKey(paLine.key); }).then(expectOk('remove'))
          .then(refreshCartUI);
      }
      return addTier(desiredTier.id).then(expectOk('add')).then(refreshCartUI);
    }).catch(function (e) {
      // A refused write must not leave the checkbox lying: re-render from the
      // server and let syncOrderProtection set it from the real cart.
      console.warn('[CartCustom] reconcile failed:', e);
      return refreshCartUI();
    }).then(function () {
      isBusy = false;
      setLoading(false);
      return getCart().then(function (cart) {
        bindOrderProtection();
        syncOrderProtection(cart);
        syncShippingBar(cart);
      });
    }).then(function () {
      if (pendingRecompute) {
        pendingRecompute = false;
        var intent = lastIntent;
        if (intent === null) {
          intent = Array.prototype.some.call(allCheckboxes(), function (cb) { return cb.checked; });
        }
        return reconcile(intent);
      }
      // Cart settled and nothing else queued: let a held checkout go.
      releaseCheckout();
    }).catch(function (e) {
      console.warn('[CartCustom] sync failed:', e);
      isBusy = false;
      setLoading(false);
      releaseCheckout();
    });
  }

  // The widget renders once in the drawer and, on /cart, once more in the
  // footer. Every state write goes to all copies; the intent comes from the
  // copy the shopper actually toggled.
  function allWidgets() { return document.querySelectorAll('[data-pa-widget]'); }
  function allCheckboxes() { return document.querySelectorAll('[data-pa-checkbox]'); }
  var lastIntent = null;

  function setLoading(state) {
    allWidgets().forEach(function (w) { w.setAttribute('data-pa-loading', state ? 'true' : 'false'); });
  }

  // Checkout while a protection write is in flight. The bag is mid-change
  // (a tier swap briefly holds two protection lines), so the submit must not
  // go through yet — but greying the button out on every quantity change
  // read as a stall. Instead the click is accepted, held, and replayed with
  // the same button once the cart settles, so `name=checkout` still sends
  // the shopper to checkout. Capture-phase listener on the document: the
  // drawer form and the /cart form are both re-rendered, so binding once
  // here survives every swap.
  var pendingCheckout = null; // { formId, buttonId, timer }

  // "Cart dirty" covers the gap BEFORE our own write: Dawn debounces a
  // quantity change for 300 ms, then fetches, then re-renders, and only then
  // does the tier swap start. A checkout tap inside that gap would post the
  // new quantity with the old tier. So a quantity edit or a trash tap marks
  // the cart dirty until the next sync after Dawn's re-render.
  var cartDirty = false;
  var dirtySince = 0;
  var DIRTY_GIVE_UP = 8000; // never strand the shopper if a sync never comes

  function isCartForm(form) {
    return !!form && (form.id === 'CartDrawer-Form' || form.id === 'cart');
  }

  function markCartDirty() {
    cartDirty = true;
    dirtySince = Date.now();
  }

  function onCartInputChange(e) {
    var t = e.target;
    if (!t || t.name !== 'updates[]') return;
    if (!isCartForm(t.form || t.closest('form'))) return;
    markCartDirty();
  }

  function onCartRemoveClick(e) {
    var t = e.target && e.target.closest ? e.target.closest('cart-remove-button') : null;
    if (!t) return;
    if (!isCartForm(t.closest('form'))) return;
    markCartDirty();
  }

  function cartSettled() {
    if (isBusy) return false;
    if (cartDirty && Date.now() - dirtySince < DIRTY_GIVE_UP) return false;
    return true;
  }

  function onCartFormSubmit(e) {
    var form = e.target;
    if (!isCartForm(form)) return;
    if (cartSettled()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var btn = e.submitter || form.querySelector('[name="checkout"]');
    if (pendingCheckout && pendingCheckout.timer) clearTimeout(pendingCheckout.timer);
    pendingCheckout = {
      formId: form.id,
      buttonId: btn ? btn.id : '',
      timer: setTimeout(function () { releaseCheckout(true); }, DIRTY_GIVE_UP)
    };
    if (btn) {
      btn.setAttribute('aria-busy', 'true');
      btn.style.opacity = '0.7';
    }
  }

  function releaseCheckout(force) {
    if (!pendingCheckout) return;
    if (!force && !cartSettled()) return;
    var p = pendingCheckout;
    pendingCheckout = null;
    if (p.timer) clearTimeout(p.timer);
    var form = document.getElementById(p.formId);
    if (!form) return;
    var btn = (p.buttonId && document.getElementById(p.buttonId)) || form.querySelector('[name="checkout"]');
    if (btn && typeof form.requestSubmit === 'function') {
      form.requestSubmit(btn);
      return;
    }
    var hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'checkout';
    form.appendChild(hidden);
    form.submit();
  }
  function setActiveVisual(active) {
    allWidgets().forEach(function (w) { w.setAttribute('data-pa-active', active ? 'true' : 'false'); });
  }

  function syncOrderProtection(cart) {
    var checkboxes = allCheckboxes();
    if (!checkboxes.length) return;

    var paLine = findPALine(cart);
    var subCents = subtotalExcludingPA(cart);
    var tier = tierForSubtotal(subCents);
    if (!tier) return;

    var newLabel = subCents > 0 ? formatMoney(priceForTier(tier)) : '';
    document.querySelectorAll('[data-pa-price-label]').forEach(function (label) {
      if (label.textContent !== newLabel) label.textContent = newLabel;
    });

    var inCart = !!paLine;
    checkboxes.forEach(function (cb) { cb.checked = inCart; });
    setActiveVisual(inCart);

    // Only self-heal when idle, so we never fight an in-flight reconcile.
    if (!isBusy) {
      if (inCart && (paLine.id !== tier.id || paLine.quantity !== 1) && subCents > 0) {
        reconcile(true);
      }
      if (inCart && subCents <= 0) {
        reconcile(false);
      }
    }
  }

  function onToggle(e) {
    lastIntent = e.target.checked;
    setActiveVisual(e.target.checked);
    reconcile(e.target.checked);
  }

  function bindOrderProtection() {
    allCheckboxes().forEach(function (cb) {
      if (cb.dataset.paBound === '1') return;
      cb.dataset.paBound = '1';
      cb.addEventListener('change', onToggle);
    });
  }

  // ========================================================================
  // FEATURE 2: FREE SHIPPING BAR
  // ========================================================================

  function syncShippingBar(cart) {
    var bar = document.querySelector('[data-fs-bar]');
    if (!bar) return;

    var threshold = parseFloat(bar.getAttribute('data-fs-threshold')) || 0;
    if (threshold <= 0) { bar.hidden = true; return; }

    var subCents = subtotalExcludingPA(cart);
    var subDollars = subCents / 100;

    // Hide entirely on empty cart.
    if (subCents <= 0) { bar.hidden = true; return; }
    bar.hidden = false;

    var msg = bar.querySelector('[data-fs-message]');
    var fill = bar.querySelector('[data-fs-fill]');
    var track = bar.querySelector('.fs-bar__track');

    var pct = Math.min(100, (subDollars / threshold) * 100);
    var pctStr = pct.toFixed(1);
    // Only touch the DOM when values actually change — identical writes still
    // fire MutationObserver records and would feed a sync loop.
    if (fill && fill.dataset.fsPct !== pctStr) {
      fill.style.setProperty('width', pct + '%', 'important');
      fill.dataset.fsPct = pctStr;
    }
    if (track) {
      var pctRound = String(Math.round(pct));
      if (track.getAttribute('aria-valuenow') !== pctRound) {
        track.setAttribute('aria-valuenow', pctRound);
      }
    }

    var stateKey, html;
    if (subDollars >= threshold) {
      stateKey = 'unlocked';
      html = '<span class="fs-amount">You\'ve unlocked FREE SHIPPING!</span>';
    } else {
      var remaining = threshold - subDollars;
      stateKey = 'away:' + formatMoney(remaining);
      html = "You're <span class=\"fs-amount\">" + formatMoney(remaining) +
             "</span> away from free shipping";
    }
    if (msg && msg.dataset.fsState !== stateKey) {
      msg.innerHTML = html;
      msg.dataset.fsState = stateKey;
    }
  }

  // ========================================================================
  // SHARED SYNC + WATCHERS
  // ========================================================================

  // Single cart fetch, then update BOTH features.
  function syncAll() {
    return getCart().then(function (cart) {
      bindOrderProtection();
      bindInfoTrigger();
      syncOrderProtection(cart);
      syncShippingBar(cart);
      // This sync follows a re-render (Dawn's or ours), so the cart reflects
      // every edit made before it. If syncOrderProtection just started a
      // swap, isBusy holds the checkout and reconcile releases it.
      cartDirty = false;
      releaseCheckout();
    }).catch(function () {});
  }

  // ========================================================================
  // iOS GHOST-OVERLAY FIX
  // ========================================================================
  // On iOS Safari, after the drawer's DOM has been swapped (add-to-cart
  // re-render) the close transition can leave a GHOSTED compositing layer:
  // computed styles say hidden, but the dim stays painted (no hit-target).
  // CSS cannot clear a ghost — the layer must be destroyed and rebuilt.
  // When the drawer finishes closing, briefly display:none the container,
  // force a reflow, and restore. Invisible to the user; kills the ghost.
  // Uses its own attribute-only observer (class changes don't fire the main
  // childList observer, and the style toggle here doesn't either — no loops).
  function watchDrawerGhost() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.__paGhostWatched) return;
    drawer.__paGhostWatched = true;
    new MutationObserver(function () {
      if (drawer.classList.contains('active')) return;
      // Wait out Dawn's close transition, then nuke the painted layer.
      // IMPORTANT: in stock Dawn the grey dim is the <cart-drawer> element's
      // OWN background (rgba shadow), not a child overlay — so the layer we
      // destroy must be the drawer element itself. Toggling a child does not
      // rebuild the parent's paint.
      setTimeout(function () {
        if (drawer.classList.contains('active')) return; // reopened meanwhile
        var prev = drawer.style.display;
        drawer.style.display = 'none';
        void drawer.offsetHeight; // force reflow — layer destroyed here
        drawer.style.display = prev || '';
      }, 450);
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  var observer = null;
  function watchCartMutations() {
    if (observer) observer.disconnect();
    observer = new MutationObserver(debounce(function () { syncAll(); }, 250));
    var targets = [
      document.querySelector('cart-drawer'),
      document.querySelector('#CartDrawer'),
      document.querySelector('#main-cart-items'),
      document.querySelector('cart-items'),
      document.querySelector('#cart')
    ].filter(Boolean);
    targets.forEach(function (t) { observer.observe(t, { childList: true, subtree: true }); });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  // ========================================================================
  // INFO MODAL — "what's included" popup
  // ========================================================================
  // The (i) button opens a centered modal. We move the modal (from the
  // <template>) to <body> so cart-drawer re-renders can't destroy it while open.

  var paModalEl = null; // the live modal element in <body>, when open

  function openModal() {
    if (paModalEl) return; // already open
    var tpl = document.querySelector('[data-pa-modal-template]');
    if (!tpl || !tpl.content) return;
    var node = tpl.content.firstElementChild.cloneNode(true);
    document.body.appendChild(node);
    paModalEl = node;
    document.body.style.overflow = 'hidden'; // lock scroll behind modal

    // Close handlers: clicking the X, the backdrop, or anywhere on the overlay
    // outside the dialog closes the modal. Clicks inside the dialog do not.
    node.querySelectorAll('[data-pa-modal-close]').forEach(function (el) {
      el.addEventListener('click', closeModal);
    });
    // Whole overlay closes on click...
    node.addEventListener('click', closeModal);
    // ...but clicks inside the dialog are stopped before they reach the overlay.
    var dlg = node.querySelector('.pa-modal__dialog');
    if (dlg) {
      dlg.addEventListener('click', function (e) { e.stopPropagation(); });
    }
    document.addEventListener('keydown', onModalKey);

    // Move focus to the dialog for accessibility
    var dialog = node.querySelector('.pa-modal__dialog');
    if (dialog) { dialog.setAttribute('tabindex', '-1'); dialog.focus(); }
  }

  function closeModal() {
    if (!paModalEl) return;
    document.removeEventListener('keydown', onModalKey);
    paModalEl.remove();
    paModalEl = null;
    document.body.style.overflow = ''; // restore scroll
  }

  function onModalKey(e) {
    if (e.key === 'Escape') closeModal();
  }

  // Bind the (i) trigger. Safe to call repeatedly (guarded).
  function bindInfoTrigger() {
    document.querySelectorAll('[data-pa-info]').forEach(function (btn) {
      if (btn.dataset.paInfoBound === '1') return;
      btn.dataset.paInfoBound = '1';
      btn.addEventListener('click', function (e) {
        // Prevent the click from toggling the checkbox (the (i) sits inside the label)
        e.preventDefault();
        e.stopPropagation();
        openModal();
      });
    });
  }

  function init() {
    syncAll();
    bindInfoTrigger();
    watchCartMutations();
    watchDrawerGhost();
    if (!document.body.dataset.paSubmitBound) {
      document.body.dataset.paSubmitBound = '1';
      document.addEventListener('submit', onCartFormSubmit, true);
      document.addEventListener('change', onCartInputChange, true);
      document.addEventListener('click', onCartRemoveClick, true);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);

})();
